/**
 * 認証コンテキスト & フック
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSession, signIn, signOut, completeNewPassword, isAuthenticated } from '../services/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const session = await getSession();
      setUser({ email: session.email });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setPendingUser(result.user);
        return { needsNewPassword: true };
      }
      setUser({ email });

      // 登録直後の場合はDynamoDB登録を実行
      const pending = sessionStorage.getItem('pendingRegistration');
      if (pending) {
        try {
          const { registerUser } = await import('../services/userApi');
          await registerUser({ email, ...JSON.parse(pending) });
          sessionStorage.removeItem('pendingRegistration');
        } catch (e) {
          console.warn('DynamoDB登録失敗:', e);
        }
      }

      return { success: true };
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  }, []);

  const setNewPassword = useCallback(async (newPassword) => {
    setError(null);
    try {
      await completeNewPassword(pendingUser, newPassword);
      setPendingUser(null);
      await checkAuth();
      return { success: true };
    } catch (err) {
      setError(err.message || 'Password change failed');
      throw err;
    }
  }, [pendingUser]);

  const logout = useCallback(() => {
    signOut();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        pendingUser,
        login,
        logout,
        setNewPassword,
        isLoggedIn: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
